// A-0008.03: Read-only metadata panel.
// Surfaces V2+ informational fields that the Server populates / manages:
//   schema_version, audit{}, resources_used{}, engine_capabilities_required{}.
// All labeled "Informational (auto-populated)" so the user knows they're not
// directly editable here.
import {
    useContext
} from 'react';
import {
 Label
} from 'shared/components/ui/label';
import {
 Badge
} from 'shared/components/ui/badge';
import {
    AuditBlock, ResourceManifest, EngineCapabilities
} from 'builders/builderOpcode/jsonMode/types';
import {
    Context
} from 'shared/context/GlobalState';

const InfoBadge = () => (
    <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
        Informational
    </Badge>
);

const Row = ({
 label, value
}: { label: string; value: React.ReactNode }) => (
    <div className="flex items-baseline gap-3 py-1">
        <Label className="text-xs text-muted-foreground w-40 shrink-0">{label}</Label>
        <div className="text-sm font-mono break-all">{value || <span className="italic text-muted-foreground">—</span>}</div>
    </div>
);

const ListRow = ({
 label, items
}: { label: string; items: string[] | undefined }) => (
    <div className="flex items-baseline gap-3 py-1">
        <Label className="text-xs text-muted-foreground w-40 shrink-0">{label}</Label>
        <div className="flex flex-wrap gap-1">
            {(!items || items.length === 0) ? (
                <span className="italic text-muted-foreground text-sm">—</span>
            ) : items.map(v => (
                <Badge key={v} variant="secondary" className="font-mono text-xs">{v}</Badge>
            ))}
        </div>
    </div>
);

const MetadataPanel = ({
    opcodeId,
    schemaVersion,
    audit,
    resourcesUsed,
    engineCapabilities,
}: {
    opcodeId?: string;
    schemaVersion?: string;
    audit?: AuditBlock;
    resourcesUsed?: ResourceManifest;
    engineCapabilities?: EngineCapabilities;
}) => {
    const {
        user
    } = useContext(Context);

    const resolveUser = (id?: string) => {
        if (!id) return undefined;
        if ((user as any)?._id === id) return (user as any)?.name ?? id;
        return id.slice(-8);
    };

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <h2 className="text-lg font-semibold mb-1">Metadata</h2>
                <p className="text-xs text-muted-foreground">
                    Server-managed fields. Auto-populated on save; shown here for visibility.
                </p>
            </div>

            <section className="space-y-1 border rounded-md p-4 bg-card">
                <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-semibold">Identity & Schema</h3>
                    <InfoBadge />
                </div>
                <Row label="opcode_id" value={opcodeId} />
                <Row label="schema_version" value={schemaVersion || '2.0'} />
            </section>

            <section className="space-y-1 border rounded-md p-4 bg-card">
                <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-semibold">Audit</h3>
                    <InfoBadge />
                </div>
                <Row label="created" value={audit?.created} />
                <Row label="createdBy" value={resolveUser(audit?.createdBy)} />
                <Row label="updated" value={audit?.updated} />
                <Row label="updatedBy" value={resolveUser(audit?.updatedBy)} />
            </section>

            <section className="space-y-1 border rounded-md p-4 bg-card">
                <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-semibold">Resources Used</h3>
                    <InfoBadge />
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                    Derived from steps on save (LLMs, APIs, collections, tools, subflows).
                </p>
                <ListRow label="llms" items={resourcesUsed?.llms} />
                <ListRow label="apis" items={resourcesUsed?.apis} />
                <ListRow label="collections" items={resourcesUsed?.collections} />
                <ListRow label="tools" items={resourcesUsed?.tools} />
                <ListRow label="subflows" items={resourcesUsed?.subflows} />
            </section>

            <section className="space-y-1 border rounded-md p-4 bg-card">
                <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-semibold">Engine Capabilities Required</h3>
                    <InfoBadge />
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                    Derived from step kinds on save (parallel, cycle_detection, streaming).
                </p>
                <Row label="min_schema_version" value={engineCapabilities?.min_schema_version} />
                <ListRow label="requires" items={engineCapabilities?.requires} />
            </section>
        </div>
    );
};

export default MetadataPanel;
